import type { FeatureContent } from './types';

export const matchInsights: FeatureContent = {
  slug: 'match-insights',
  path: '/features/match-insights',
  audience: 'candidate',
  icon: 'ScanSearch',
  name: 'Match Insights',
  navLabel: 'Match Insights',
  navDescription: 'See exactly why you match each role. No black box.',
  primaryKeyword: 'job match transparency',
  seo: {
    title: 'Match Insights: See Why You Match | Talenture.ai',
    description:
      'See exactly why you match each role: a transparent score across skills, experience, culture, and logistics, with the evidence behind every number. No black box.',
    keywords: [
      'job match transparency',
      'job match score',
      'why am I a match',
      'AI job match explanation',
      'skills match breakdown',
      'transparent candidate matching',
    ],
    ogTitle: 'See Exactly Why You Match | Talenture.ai',
    ogDescription:
      'A transparent match score across skills, experience, culture, and logistics, with the evidence behind every signal. No black box, no guessing.',
  },
  hero: {
    eyebrow: 'For candidates',
    h1: 'See exactly why you match. No black box',
    subhead:
      'Most hiring tools score you in secret. Talenture shows you the whole picture: which skills matched, where the gaps are, and how every signal added up, so you never have to guess whether you are qualified.',
    primaryCta: { label: 'Check your CV free', href: '/cv-vs-market' },
    secondaryCta: { label: 'Create free account', href: '/candidate-auth' },
  },
  definition: {
    heading: 'What is a transparent job match?',
    body: 'A transparent job match shows the reasoning behind your fit for a role, not just a number. Talenture breaks your match into technical skills, experience, culture, and logistics, shows which of your skills bridged each requirement, and ties the result to real evidence from your CV, so you can see and act on exactly where you stand.',
  },
  sections: [
    {
      icon: 'PieChart',
      title: 'A score you can read, not just a number',
      body: 'Your overall match from 0 to 100 breaks down into the parts that built it, so a single figure never hides the full story.',
      bullets: [
        'Technical skills',
        'Experience',
        'Culture and values',
        'Logistics and compatibility',
      ],
    },
    {
      icon: 'Shuffle',
      title: 'Direct and related skills, both shown',
      body: 'You get credit for transferable strengths, and you can see it happen. When one of your skills bridges a requirement, Talenture shows the link instead of marking you down for a missing exact match.',
      bullets: [
        'See which skill satisfied each requirement',
        'Example: counted as React via your Vue.js experience',
        'Built on a graph of 21,700+ skill relationships',
      ],
    },
    {
      icon: 'Filter',
      title: 'The rules, in the open',
      body: 'If a role has hard requirements on seniority, location, or salary, you see them and whether you passed. When you are shown for flexibility despite a rule, Talenture tells you that too, so nothing is hidden.',
      bullets: [
        'Seniority, location, and salary rules made visible',
        'Clear pass or flexibility status on each',
        'Example: salary outside budget, shown for flexibility',
      ],
    },
    {
      icon: 'Quote',
      title: 'Evidence, not generic AI text',
      body: 'Your strengths and gaps are tied to specific points in your CV, written in plain language, so the feedback feels like it is actually about you, because it is.',
    },
    {
      icon: 'Compass',
      title: 'Signals working under the hood',
      body: 'Behind the breakdown, Talenture weighs several real signals so the score reflects more than keyword overlap.',
      bullets: [
        'Semantic similarity between your profile and the role',
        'Distance between skills in the taxonomy graph',
        'Commute distance and salary-range overlap',
      ],
    },
  ],
  howTo: {
    heading: 'How Match Insights explains your fit',
    intro: 'Three steps from a match score to clear evidence and next actions.',
    steps: [
      {
        title: 'Break the match into four dimensions',
        body: 'Talenture scores technical skills, experience, culture and values, and logistics so the overall result never hides the full picture.',
      },
      {
        title: 'Connect requirements to your evidence',
        body: 'The match shows which direct or related skills satisfy each requirement and ties strengths and gaps to specific points in your CV.',
      },
      {
        title: 'Make the rules and gaps visible',
        body: 'Seniority, location, salary, and flexibility rules are shown with a clear pass or flexibility status, so you know exactly what lifted or lowered the score.',
      },
    ],
  },
  proofPoints: [
    { value: '4', label: 'dimensions in every breakdown' },
    { value: '21,700+', label: 'skill relationships behind the match' },
    { value: '0 to 100', label: 'score, fully explained' },
  ],
  faq: [
    {
      question: 'Why can I not see why I match on other platforms?',
      answer:
        'Most tools give you a single score or nothing at all, with no reasoning. Talenture shows the full breakdown across skills, experience, culture, and logistics, plus the evidence behind each signal, so you understand exactly where you stand instead of guessing.',
    },
    {
      question: 'What does the match score actually measure?',
      answer:
        'Your overall score from 0 to 100 is built from technical skills, experience, culture and values, and logistics like location and salary fit. Each part is shown separately, so you can see what lifted your score and what held it back.',
    },
    {
      question: 'Do I get credit for transferable skills?',
      answer:
        'Yes. Talenture uses a graph of 21,700+ skill relationships to recognise when one of your skills bridges a requirement, for example counting Vue.js experience toward a React requirement. You can see exactly which skill satisfied each requirement.',
    },
    {
      question: 'What if I do not meet a hard requirement?',
      answer:
        'You still see the rule and your status against it. Sometimes you are shown to a hiring manager for flexibility even when a rule is not met, for example a salary just outside budget, and Talenture tells you that rather than silently filtering you out.',
    },
    {
      question: 'Is the feedback specific to me?',
      answer:
        'Yes. Strengths and gaps are anchored to specific evidence in your CV and written in plain language. It is not generic AI text, it reflects your actual experience against the actual role.',
    },
    {
      question: 'How do I close the gaps it finds?',
      answer:
        'Match Insights pairs with upskilling recommendations, which turn the gaps you see here into a prioritised learning plan. You find out where you stand, then get a concrete path to improve.',
    },
  ],
  relatedSlugs: ['upskilling-recommendations', 'application-tracking', 'ai-candidate-matching'],
};
